import { useState, useEffect, useCallback } from "react";
import { UserCog, Loader2, Plus, Trash2, ShieldCheck, User } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";

const TeamTab = ({ token }) => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "assistant" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setStaff(await apiFetch("/admin/staff", { token })); } catch (e) { /* */ } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.email || form.password.length < 6) { toast.error("Vyplňte e-mail a heslo (min. 6 znaků)."); return; }
    setSaving(true);
    try { await apiFetch("/admin/staff", { method: "POST", token, body: form }); toast.success("Přístup vytvořen."); setForm({ name: "", email: "", password: "", role: "assistant" }); load(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!(await confirmDialog({ title: `Smazat ${u.name || u.email}?`, confirmText: "Smazat", danger: true }))) return;
    try { await apiFetch(`/admin/staff/${u.id}`, { method: "DELETE", token }); toast.success("Smazáno."); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-orange-400 animate-spin" /></div>;

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="team-tab">
      <div className="lg:col-span-1">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-1 inline-flex items-center gap-2"><Plus className="w-5 h-5 text-orange-400" /> Přidat asistentku</h3>
          <div className="grid sm:grid-cols-2 gap-2 mb-3" data-testid="staff-role-picker">
            {[["assistant", "Asistent/ka", "Poptávky, kalendář, klienti, smlouvy, vozy, Foto studio, Flotila, Výplaty (zápis a schválení). Nevidí Náklady a Tabulky, nemůže mazat ani potvrzovat platby a vyplácet."],
              ["accountant", "Účetní", "Faktury (vystavení, editace, odeslání), Výplaty (schválení i vyplacení), Náklady, Tabulky a Přehled financí. Klienty, smlouvy a vozy vidí jen pro čtení."]].map(([k, l, d]) => (
              <button type="button" key={k} onClick={() => setForm({ ...form, role: k })} className={`text-left rounded-xl border p-3 transition-colors ${form.role === k ? "border-orange-500/60 bg-orange-500/10" : "border-white/10 hover:bg-white/5"}`} data-testid={`staff-role-${k}`}>
                <p className="text-sm font-semibold">{l}</p><p className="text-[11px] text-zinc-500 mt-1">{d}</p>
              </button>
            ))}
          </div>
          <form onSubmit={add} className="space-y-3" data-testid="staff-form">
            <input className="input-dark" placeholder="Jméno" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
            <input className="input-dark" type="email" placeholder="E-mail *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
            <input className="input-dark" type="text" placeholder="Heslo (min. 6 znaků) *" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" />
            <button type="submit" disabled={saving} className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="staff-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Vytvořit účet
            </button>
          </form>
        </div>
      </div>

      <div className="lg:col-span-2">
        <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-4 inline-flex items-center gap-2"><UserCog className="w-5 h-5 text-orange-400" /> Tým ({staff.length})</h3>
        <div className="space-y-2" data-testid="staff-list">
          {staff.map((u) => (
            <div key={u.id} className="glass rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`staff-${u.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${u.role === "admin" ? "bg-orange-500/20 text-orange-400" : "bg-sky-500/15 text-sky-400"}`}>
                  {u.role === "admin" ? <ShieldCheck className="w-5 h-5" /> : <User className="w-5 h-5" />}
                </div>
                <div className="min-w-0">
                  <p className="font-medium truncate">{u.name || u.email}</p>
                  <p className="text-xs text-zinc-500 truncate">{u.email} · {u.role === "admin" ? "Administrátor" : u.role === "accountant" ? "Účetní" : "Asistent/ka"}</p>
                </div>
              </div>
              {(u.role === "assistant" || u.role === "accountant") && (
                <button onClick={() => remove(u)} className="text-zinc-500 hover:text-red-400 p-2 shrink-0" data-testid={`staff-delete-${u.id}`} aria-label="Smazat"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeamTab;
